import React, { ReactNode, useEffect, useState } from 'react';

import styled from 'styled-components';

import Button from 'components/lib/Button';
import Checkbox from 'components/lib/Checkbox';
import Modal from 'components/lib/Modal';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

const Description = styled.div`
  color: var(--text-secondary);
  white-space: pre-wrap;
`;

const Content = styled.div`
  display: flex;
`;

const StyledButton = styled(Button)`
  min-width: 120px;
  border-radius: 4px;
`;

const StyledModal = styled(Modal)`
  .ant-btn-primary:disabled {
    opacity: 0.3;
    color: #fff;
    background: #275aff;
  }
`;

const CheckboxWrapper = styled.div`
  .ant-checkbox-wrapper {
    margin-top: 25px;
    padding-bottom: 40px;
  }

  .ant-checkbox-label {
    font-size: 14px;
  }
`;

interface Props {
  open: boolean;
  title: string | ReactNode;
  subtitle?: string | ReactNode | undefined;
  subtitleStyle?: React.CSSProperties;
  description: ReactNode;
  children?: ReactNode;
  loading?: boolean;
  closable?: boolean;
  maskClosable?: boolean;
  keyboard?: boolean;
  confirmLabel: string;
  onConfirm: any;
  onCancel?: any;
  disabled?: boolean;
  checkboxConfirmLabel?: string;
  confirmButtonDataTestId?: string;
  cancelButtonDataTestId?: string;
}

const ConfirmModal = ({
  open,
  title,
  subtitle,
  subtitleStyle,
  description,
  loading,
  closable = true,
  maskClosable = true,
  keyboard = true,
  confirmLabel,
  onConfirm,
  onCancel,
  disabled = false,
  children,
  checkboxConfirmLabel,
  confirmButtonDataTestId,
  cancelButtonDataTestId,
}: Props) => {
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    setConfirmed(Boolean(!checkboxConfirmLabel));
  }, [open, checkboxConfirmLabel]);

  return (
    <StyledModal
      centered
      open={open}
      title={
        <div>
          <div>{title}</div>
          <div
            style={{
              fontSize: '14px',
              fontFamily: 'var(--font-regular)',
              color: 'var(--dark-blue)',
              ...subtitleStyle,
            }}
          >
            {subtitle}
          </div>
        </div>
      }
      className={'compact'}
      closable={closable}
      maskClosable={maskClosable}
      keyboard={keyboard}
      destroyOnClose={true}
      onCancel={onCancel}
      footer={[
        <StyledButton
          key="confirm"
          type="primary"
          loading={loading}
          disabled={disabled || !confirmed}
          onClick={onConfirm}
          data-test-id={confirmButtonDataTestId}
        >
          {confirmLabel}
        </StyledButton>,
        <StyledButton key="cancel" disabled={loading} onClick={onCancel} data-test-id={cancelButtonDataTestId}>
          Cancel
        </StyledButton>,
      ]}
    >
      <Container>
        <Description>{description}</Description>
        <Content>{children}</Content>
        {checkboxConfirmLabel && (
          <CheckboxWrapper>
            <Checkbox checked={confirmed} className={'green'} onChange={(e: any) => setConfirmed(e.target.checked)}>
              {checkboxConfirmLabel}
            </Checkbox>
          </CheckboxWrapper>
        )}
      </Container>
    </StyledModal>
  );
};

export default ConfirmModal;
